import React, { useState } from 'react';
import { Customer } from '@/hooks/useCustomers';
import { useSourceManager } from '@/hooks/useSourceManager';
import { useGmailStats, formatTimeAgo } from '@/hooks/useGmailStats';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface MailboxSourceCardProps {
  customer: Customer;
  onSetupMode?: (mode: string) => void;
}

export const MailboxSourceCard: React.FC<MailboxSourceCardProps> = ({ customer }) => {
  const { toast } = useToast();
  const { sources, enableSource, disableSource, testSource, loading } = useSourceManager(customer.id);
  const { emailStats, refreshStats } = useGmailStats(customer.id);
  const [isTestingEmail, setIsTestingEmail] = useState(false);

  const mailboxAddress = `bills+${customer.name.toLowerCase().replace(/\s+/g, '')}@billportal.ai`;
  const sourceStatus = sources?.mailbox?.status || 'active';
  const isActive = sourceStatus === 'active';

  // Handle test email
  const handleSendTestEmail = async () => {
    setIsTestingEmail(true);
    try {
      const success = await testSource('mailbox');
      if (success) {
        await refreshStats();
      }
    } finally {
      setIsTestingEmail(false);
    }
  };

  // Handle enable/disable
  const handleToggleSource = async () => {
    if (isActive) {
      await disableSource('mailbox');
    } else {
      await enableSource('mailbox', { auto_enabled: true });
    }
  };

  // Handle manual email sync
  const handleSyncEmails = async () => {
    try {
      const response = await supabase.functions.invoke('admin-gmail-run-ingest', {
        body: { 
          customerFilter: customer.id,
          lookbackDays: 7
        }
      });
      
      if (response.data?.success) {
        toast({
          title: "Sync Complete",
          description: 'Email sync completed successfully!',
        });
        await refreshStats();
      } else {
        toast({
          title: "Sync Failed",
          description: 'Email sync failed',
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Sync Failed",
        description: 'Email sync failed',
        variant: "destructive",
      });
    }
  };

  return (
    <div className={`bg-card rounded-lg p-4 shadow-sm transition-all ${
      isActive ? 'border-2 border-green-500/20' : 'border border-border'
    }`}>
      {/* Header with Status Toggle */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            isActive ? 'bg-green-500/10' : 'bg-muted'
          }`}>
            <span className="text-xl">📧</span>
          </div>
          <div>
            <h3 className="font-medium text-foreground">Default Mailbox</h3>
            <p className="text-sm text-muted-foreground">
              {isActive ? 'Automatic email forwarding (Active)' : 'Automatic email forwarding (Disabled)'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-1 rounded text-xs font-medium ${
            isActive 
              ? 'bg-green-500/10 text-green-600' 
              : 'bg-muted text-muted-foreground'
          }`}>
            {isActive ? '✅ Active (Default)' : '⭕ Disabled'}
          </span>
          <button
            onClick={handleToggleSource}
            disabled={loading}
            className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
              isActive
                ? 'bg-red-100 text-red-700 hover:bg-red-200'
                : 'bg-green-100 text-green-700 hover:bg-green-200'
            }`}
          >
            {isActive ? 'Disable' : 'Enable'}
          </button>
        </div>
      </div>

      {isActive && (
        <>
        {/* Email Address Section */}
        <div className="mb-4 p-3 bg-green-500/5 rounded border border-green-500/20">
          <div className="text-xs text-green-700 font-medium mb-1">Customer Email Address</div>
          <div className="flex items-center gap-2">
            <code className="text-sm font-mono text-green-600 flex-1 bg-card px-2 py-1 rounded">
              {mailboxAddress}
            </code>
            <button 
              onClick={() => {
                navigator.clipboard.writeText(mailboxAddress);
                toast({
                  title: "Copied",
                  description: 'Email address copied!',
                });
              }}
              className="text-green-600 text-xs hover:underline"
            >
              Copy
            </button>
          </div>
          <p className="text-xs text-green-600 mt-1">
            📨 Bills sent to this address are automatically processed for {customer.name}
          </p>
        </div>

        {/* Real-time Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="text-center p-3 bg-blue-500/5 rounded border border-blue-500/20">
            <div className="text-xs text-blue-700">Emails Processed</div>
            <div className="text-lg font-semibold text-blue-900">{emailStats.totalProcessed || 0}</div>
            <div className="text-xs text-blue-600">All time</div>
          </div>
          <div className="text-center p-3 bg-purple-500/5 rounded border border-purple-500/20">
            <div className="text-xs text-purple-700">This Week</div>
            <div className="text-lg font-semibold text-purple-900">{emailStats.thisWeek || 0}</div>
            <div className="text-xs text-purple-600">New emails</div>
          </div>
          <div className="text-center p-3 bg-orange-500/5 rounded border border-orange-500/20">
            <div className="text-xs text-orange-700">Last Email</div>
            <div className="text-sm font-medium text-orange-900">
              {emailStats.lastEmailTime ? formatTimeAgo(emailStats.lastEmailTime) : 'Never'}
            </div>
            <div className="text-xs text-orange-600">Received</div>
          </div>
        </div>

        {/* Setup Instructions */}
      <div className="p-3 bg-muted rounded mb-4">
        <div className="text-xs font-medium text-foreground mb-2">📋 Setup Instructions</div>
        <ul className="text-xs text-muted-foreground space-y-1">
          <li>• ✅ This mailbox is automatically enabled for every customer</li>
          <li>• 📧 Forward utility bills to the address above</li>
          <li>• 📎 PDF/image attachments will be automatically processed</li>
          <li>• 📊 Maximum file size: 25MB per attachment</li>
          <li>• 🔄 Processing typically takes 1-2 minutes</li>
        </ul>
      </div>

        {/* Functional Actions */}
        <div className="flex gap-2 flex-wrap">
          <button 
            onClick={handleSendTestEmail}
            disabled={isTestingEmail || loading}
            className="bg-green-600 text-white px-3 py-2 rounded text-sm hover:bg-green-700 disabled:opacity-50"
          >
            {isTestingEmail ? '📧 Testing...' : '📧 Send Test Email'}
          </button>
          <button 
            onClick={handleSyncEmails}
            disabled={loading}
            className="bg-blue-600 text-white px-3 py-2 rounded text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            🔄 Sync Emails Now
          </button>
          <button 
            onClick={() => {/* Open email history modal */}}
            className="border border-border text-foreground px-3 py-2 rounded text-sm hover:bg-muted"
          >
            📊 View Email History
          </button>
          <button 
            onClick={() => {/* Open email settings modal */}}
            className="border border-border text-foreground px-3 py-2 rounded text-sm hover:bg-muted"
          >
            ⚙️ Email Settings
          </button>
        </div>
        </>
      )}

      {!isActive && (
        <div className="text-center py-8">
          <div className="text-4xl mb-2">📧</div>
          <p className="text-muted-foreground mb-4">Mailbox source is currently disabled</p>
          <button
            onClick={handleToggleSource}
            disabled={loading}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50"
          >
            Enable Mailbox
          </button>
        </div>
      )}
    </div>
  );
};